import { useCallback, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { GET_TALENT_FOR_TALENT_BOARD, SYNCHRO_TALENT_FROM_ATS } from '@bloomays-lib/adapter.api-talent';
import { ApolloGqlTalentForTalentBoard } from '@bloomays-lib/types.shared';
import { Logger } from '../services/service-logger';
import { useTalentsForTalentBoardStore } from '../stores/talentsForTalentBoardStore';
import { useTalentBoardStore } from '../stores/talentBoardStore';

const logger = Logger('useSynchroTalentFromATS');

type SynchroTalentFromATSResult = {
  talent?: {
    success: boolean;
    errors?: string[];
  };
  resume?: {
    success: boolean;
    errors?: string[];
  };
  notes?: {
    success: boolean;
    errors?: string[];
  };
};

type GetTalentForTalentBoardResponse = {
  getTalentForTalentBoard: ApolloGqlTalentForTalentBoard;
};

type SynchroTalentFromATSVariables = {
  talentId: string;
  projectId: string;
};

type UseSynchroTalentFromATSParams = {
  talentId: string;
  jobId: string;
};

export const useSynchroTalentFromATS = ({ talentId, jobId }: UseSynchroTalentFromATSParams) => {
  const { updateTalent } = useTalentsForTalentBoardStore();
  const { setSelectedTalent } = useTalentBoardStore();
  const [synchroTalentFromATSMutation, { loading, error }] = useMutation<
    { synchroTalentFromATS: SynchroTalentFromATSResult },
    SynchroTalentFromATSVariables
  >(SYNCHRO_TALENT_FROM_ATS, {
    context: { clientName: 'api.talents' },
    refetchQueries: [
      {
        query: GET_TALENT_FOR_TALENT_BOARD,
        variables: { talentId, jobId },
        context: { clientName: 'api.talents' },
      },
    ],
    awaitRefetchQueries: true,
  });

  // useQuery pour écouter les changements du cache Apollo après le refetch
  // et mettre à jour les stores automatiquement
  const { data } = useQuery<GetTalentForTalentBoardResponse>(GET_TALENT_FOR_TALENT_BOARD, {
    variables: { talentId, jobId },
    context: { clientName: 'api.talents' },
    fetchPolicy: 'cache-only', // Utilise uniquement le cache, ne fait pas de requête réseau
    // Le refetchQueries va mettre à jour le cache, et ce useQuery écoutera les changements
  });

  // Met à jour les stores quand les données changent dans le cache Apollo
  useEffect(() => {
    if (data?.getTalentForTalentBoard) {
      const updatedTalent = data.getTalentForTalentBoard;
      updateTalent(jobId, updatedTalent);
      setSelectedTalent(updatedTalent);
    }
  }, [data, jobId, updateTalent, setSelectedTalent]);

  const execute = useCallback(async (): Promise<SynchroTalentFromATSResult | null> => {
    try {
      const result = await synchroTalentFromATSMutation({
        variables: {
          talentId,
          projectId: jobId,
        },
      });

      const synchroResult = result.data?.synchroTalentFromATS;
      logger.info('Synchro from ATS completed', { result: synchroResult });

      return synchroResult ?? null;
    } catch (err) {
      logger.error('Error synchro from ATS', { talentId, projectId: jobId }, err as Error);
      return null;
    }
  }, [synchroTalentFromATSMutation, talentId, jobId]);

  return {
    synchroTalentFromATS: execute,
    loading,
    error,
  };
};

export default useSynchroTalentFromATS;
